import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ThreadPanel, type MessageTransport } from '@speles7172/message-console';
import '@speles7172/message-console/styles.css';
import { SystemRole, hasMinimumRole } from '@peles/shared';
import { useAuth } from '../../context/AuthContext';
import { createRecordTransport, type RecordScope } from '../../api/notifications';
import {
  fetchAttachmentUrl,
  fetchThreadContext,
  type ThreadContext,
  type ThreadUploadLinkInput,
} from '../../api/threadTools';
import { checkGrammar } from '../../api/ai';
import { DocumentPreviewModal } from '../expenses/ExpenseDocumentsSection';
import type { PreviewableDocument } from '../documents/types';
import { ThreadTools } from './ThreadTools';
import { useCan } from '../../hooks/useCan';

export interface ConversationViewProps {
  threadId: string;
  /**
   * The record the conversation is about, when the caller already knows it —
   * a record's own panel does. Otherwise it is read from the thread's context,
   * so a conversation opened from the inbox or the bell still offers that
   * record's people and files.
   */
  record?: RecordScope | null;
  /**
   * Opened from a record's own page: until the thread's record is known —
   * handed in, or read from its context — "Add people" offers nobody, rather
   * than searching everyone the caller can reach. A context that says the
   * conversation is about no record lets the search run as the inbox's does. The inbox and the bell leave
   * it off, since a conversation there may be about no record at all.
   */
  requireRecord?: boolean;
  /** Above the transcript — a record's conversation switcher, say. */
  header?: ReactNode;
  onClose?: () => void;
}

/**
 * One open conversation: the package's `<ThreadPanel>` with the tools this
 * application adds beside it — attach from the library, attach a report, ask
 * for a file back — and a preview for anything attached.
 *
 * It exists so that a conversation is the same thing wherever it is opened.
 * The tools used to be mounted only on a record's panel, so the same thread
 * opened from Communications or the bell had no way to attach the summary or
 * ask for the signed contract; now every one of those renders this.
 *
 * ⚠️ **The tools are a producer's.** The bell is everybody's, clients
 * included, and a report or a library folder is the office's to hand out — so
 * below PLANNER the conversation is the package's alone, as it always was.
 *
 * ⚠️ **Where the files live is answered by the server** (`GET
 * /threads/:id/context`), never derived here: an expense's files are the
 * event's, and that walk is a fact about the schema, not the screen.
 */
export function ConversationView({
  threadId,
  record,
  requireRecord = false,
  header,
  onClose,
}: ConversationViewProps) {
  const { user } = useAuth();
  const [context, setContext] = useState<ThreadContext | null>(null);
  /** Whether the thread's context has been read — it may say there is no record. */
  const [contextRead, setContextRead] = useState(false);
  /**
   * Bumped whenever the toolbar puts something on the thread: `<ThreadPanel>`
   * exposes no reload, so a remount is how a newly attached file appears.
   */
  const [reloadKey, setReloadKey] = useState(0);
  /**
   * A file request staged to ride the next message, because the message box
   * is the package's and nothing beside it can add to what is being typed.
   */
  const [stagedUpload, setStagedUpload] = useState<ThreadUploadLinkInput | null>(null);
  const [preview, setPreview] = useState<PreviewableDocument | null>(null);

  const producer = user != null && hasMinimumRole(user.systemRole, SystemRole.PLANNER);
  const canAttach = useCan('conversations.attach');

  useEffect(() => {
    setStagedUpload(null);
    setPreview(null);
    let live = true;
    setContextRead(false);
    fetchThreadContext(threadId)
      .then((resolved) => {
        if (!live) return;
        setContext(resolved);
        setContextRead(true);
      })
      .catch(() => {
        if (live) setContext(null);
      });
    return () => {
      live = false;
    };
  }, [threadId]);

  // Withdrawn while a file request was staged: the request goes with it.
  useEffect(() => {
    if (!canAttach) setStagedUpload(null);
  }, [canAttach]);

  const scopeType = record?.entityType ?? context?.entityType ?? null;
  const scopeId = record?.entityId ?? context?.entityId ?? null;

  /**
   * ⚠️ **Memoised on strings.** Every component in `message-console` keys its
   * effects on the transport's identity; a new object each render is an
   * endless reload.
   */
  const transport = useMemo(() => {
    const built = createRecordTransport(
      scopeType && scopeId ? { entityType: scopeType, entityId: scopeId } : null,
      {
        // The dropzone uploads onto the thread — `conversations.attach`, like the toolbar.
        canUpload: canAttach && context?.nodeId != null,
        threadId,
        // A request staged before the permission was withdrawn must not ride on
        // every later reply and have each refused.
        uploadRequest: canAttach ? stagedUpload : null,
        onUploadRequestSent: () => setStagedUpload(null),
      },
    );
    const people = built.people;
    const scoped: MessageTransport =
      requireRecord && !(scopeType && scopeId) && !contextRead && people
        ? {
            ...built,
            people: {
              ...people,
              search: async (query) => ({
                options: [],
                hasMore: false,
                limit: query.limit ?? 0,
                offset: query.offset ?? 0,
              }),
            },
          }
        : built;
    if (canAttach) return scoped;
    // Taking a file off is the same permission as putting one on; without the
    // callback the panel offers no remove.
    return { ...scoped, removeAttachment: undefined };
  }, [
    scopeType,
    scopeId,
    context?.nodeId,
    threadId,
    stagedUpload,
    canAttach,
    requireRecord,
    contextRead,
  ]);

  /** The thread a preview was asked for, so a late answer for another is dropped. */
  const threadRef = useRef(threadId);
  threadRef.current = threadId;

  const openAttachment = useCallback(
    async (attachment: { id: string; filename: string; contentType?: string | null }) => {
      const forThread = threadId;
      const url = await fetchAttachmentUrl(forThread, attachment.id);
      if (url === null || threadRef.current !== forThread) return;
      setPreview({
        id: attachment.id,
        fileName: attachment.filename,
        mimeType: attachment.contentType ?? '',
        downloadUrl: url,
      });
    },
    [threadId],
  );

  if (!user) return null;

  return (
    <div className="peles-controls" data-testid="conversation-view">
      {header}
      <ThreadPanel
        key={reloadKey}
        transport={transport}
        threadId={threadId}
        viewer={{ id: user.id }}
        onCheckGrammar={async (body) => (await checkGrammar(body)).corrected}
        onOpenAttachment={(attachment) => void openAttachment(attachment)}
        {...(onClose ? { onClose } : {})}
      />
      {producer && (
        <ThreadTools
          threadId={threadId}
          context={context}
          onChanged={() => setReloadKey((key) => key + 1)}
          onStageUpload={setStagedUpload}
        />
      )}
      {preview !== null && (
        <DocumentPreviewModal document={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
